import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Link2,
  Loader2,
  Plus,
  Search,
  Wallet,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NumberInput } from '@/components/ui/number-input';
import { Combobox } from '@/components/ui/combobox';
import { usePermission } from '@/components/guards/permission-guard';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { NewCariDialog } from '@/components/trading/cari/new-cari-dialog';
import { CariCreateModals } from '@/components/trading/cari/cari-create-modals';
import { cariTypeLabel } from '@/components/trading/cari/cari-type-display';
import { GroupedBankMatching } from './grouped-matching';
import { AccountBankMatching } from './account-matching';
type BankRow = Awaited<
  ReturnType<ReturnType<typeof trpc.useUtils>['tradingBankMatching']['list']['fetch']>
>['items'][number];
type Mode = 'create' | 'existing' | 'nonCari' | 'account';
const selectClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm';
const money = (amount: number, currency: string) =>
  `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
const day = (date: Date | string | null) =>
  date ? new Date(date).toLocaleDateString('tr-TR', { timeZone: 'UTC' }) : '—';

export function BankMatchingPage() {
  const { t, i18n } = useTranslation('trading');
  const canCreateCustomer = usePermission('CUSTOMERS', 'CREATE', 'TRADING');
  const canCreateSupplier = usePermission('SUPPLIERS', 'CREATE', 'TRADING');
  const canViewAccounting = usePermission('ACCOUNTING', 'VIEW', 'TRADING');
  const readOnlyPreview =
    import.meta.env.DEV && import.meta.env.VITE_DEV_PREVIEW_READ_ONLY === 'true';
  const canSave = canCreateCustomer && !readOnlyPreview;
  const canCreateCari = (canCreateCustomer || canCreateSupplier) && !readOnlyPreview;
  const utils = trpc.useUtils();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'groups' | 'entries'>('groups');
  const [status, setStatus] = useState<'pending' | 'completed' | 'all'>('pending');
  const [currency, setCurrency] = useState('');
  const [bankId, setBankId] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<BankRow | null>(null);
  const [targetValue, setTargetValue] = useState('');
  const [mode, setMode] = useState<Mode>('create');
  const [amount, setAmount] = useState<number | undefined>();
  const [existingId, setExistingId] = useState('');
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [accountSaving, setAccountSaving] = useState(false);
  const [newCariOpen, setNewCariOpen] = useState(false);
  const [createType, setCreateType] = useState<string | null>(null);
  const detailRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (selected && window.innerWidth < 1280)
      detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [selected?.id]);
  const filters = {
    search: search || undefined,
    status,
    currency: (currency || undefined) as 'TRY' | 'EUR' | 'USD' | 'GBP' | undefined,
    bankId: bankId || undefined,
  };
  const list = trpc.tradingBankMatching.list.useQuery(
    { ...filters, page, pageSize: 20 },
    { enabled: view === 'entries' }
  );
  const groups = trpc.tradingBankMatching.groups.useQuery(
    { ...filters, page, pageSize: 20 },
    { enabled: view === 'groups' }
  );
  const viewData = view === 'groups' ? groups.data : list.data;
  const caris = trpc.tradingCari.list.useQuery({ all: true, isActive: true });
  const types = trpc.tradingCariType.list.useQuery();
  const target = caris.data?.items.find((p) => `${p.source}:${p.id}` === targetValue);
  const preview = trpc.tradingBankMatching.preview.useQuery(
    {
      bankMovementId: selected?.id ?? '',
      target: { source: target?.source ?? 'customer', id: target?.id ?? '' },
      amount,
    },
    {
      enabled:
        !!selected &&
        selected.status === 'pending' &&
        !!target &&
        (mode === 'create' || mode === 'existing') &&
        !!amount &&
        amount > 0 &&
        amount <= selected.amount,
    }
  );
  const save = trpc.tradingBankMatching.save.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.tradingBankMatching.invalidate(), utils.tradingCari.invalidate()]);
      showSuccessToast(t('bankMatching.saved', 'Eşleştirme kaydedildi.'));
      setSelected(null);
      setError('');
    },
    onError: (e) => setError(e.message),
  });
  const choose = (row: BankRow) => {
    if (save.isPending || accountSaving) return;
    setSelected(row);
    setTargetValue('');
    setAmount(row.amount);
    setMode('create');
    setExistingId('');
    setAllowDuplicate(false);
    setNote('');
    setError('');
  };
  const changeFilter = (action: () => void) => {
    action();
    setPage(1);
  };
  const remainder = selected && amount !== undefined ? Math.max(0, selected.amount - amount) : 0;
  const candidates = preview.data?.existingCandidates ?? [];
  const needsNote = mode === 'nonCari' || remainder > 0;
  const valid =
    !!selected &&
    selected.status === 'pending' &&
    canSave &&
    !save.isPending &&
    (!needsNote || !!note.trim()) &&
    (mode === 'nonCari' ||
      (!!amount &&
        amount > 0 &&
        amount <= selected.amount &&
        !!target &&
        !!preview.data &&
        !preview.isFetching &&
        !preview.error &&
        (mode === 'existing'
          ? candidates.some((c) => c.id === existingId)
          : candidates.length === 0 || allowDuplicate)));
  const submit = () => {
    if (!valid || !selected || mode === 'account') return;
    setError('');
    save.mutate({
      bankMovementId: selected.id,
      mode,
      target: mode === 'nonCari' || !target ? undefined : { source: target.source, id: target.id },
      amount: mode === 'nonCari' ? undefined : amount,
      existingMovementId: mode === 'existing' ? existingId : undefined,
      allowDuplicate: mode === 'create' && allowDuplicate,
      note: note.trim() || undefined,
    });
  };
  const options = (caris.data?.items ?? [])
    .filter((p) => !(p.source === 'supplier' && p.id === selected?.bankId))
    .map((p) => ({
      value: `${p.source}:${p.id}`,
      label: p.companyName,
      description: `${p.cariType}${p.legacyCariCode ? ` · ${p.legacyCariCode}` : ''}`,
      keywords: [p.companyName, p.cariType, p.legacyCariCode ?? '', p.contactName ?? ''],
    }));
  const pending = viewData?.pendingCount ?? 0;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {t('bankMatching.title', 'Banka Hareketi Eşleştirme')}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {t(
              'bankMatching.subtitle',
              'Banka hareketini cari veya muhasebe hesap numarasıyla eşleştirin, etkisini kontrol edin ve kaydedin.'
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/trading/cari/transactions">
              {t('bankMatching.movements', 'Cari Hareketler')}
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/trading/cari/balances">{t('bankMatching.balances', 'Cari Bakiyeleri')}</Link>
          </Button>
          {canCreateCari && (
            <Button variant="outline" onClick={() => setNewCariOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('bankMatching.newCari', 'Yeni Cari')}
            </Button>
          )}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { label: t('bankMatching.pending', 'Eşleştirme bekleyen'), count: pending, icon: Wallet },
          {
            label: t('bankMatching.completed', 'Tamamlanan'),
            count: viewData?.completedCount ?? 0,
            icon: CheckCircle2,
          },
          {
            label: t('bankMatching.total', 'İncelenecek toplam hareket'),
            count: viewData?.totalReviewCount ?? 0,
            icon: Link2,
          },
        ].map(({ label, count, icon: Icon }) => (
          <div
            key={label}
            className="bg-card flex items-center justify-between rounded-lg border p-4"
          >
            <div>
              <p className="text-muted-foreground text-xs">{label}</p>
              <p className="mt-1 text-2xl font-semibold">{count}</p>
            </div>
            <Icon className="text-muted-foreground h-6 w-6" />
          </div>
        ))}
      </div>
      <div
        className="flex gap-2"
        role="group"
        aria-label={t('bankMatching.viewMode', 'Eşleştirme görünümü')}
      >
        <Button
          size="sm"
          variant={view === 'groups' ? 'default' : 'outline'}
          onClick={() => {
            setView('groups');
            setPage(1);
            setSelected(null);
          }}
        >
          {t('bankMatching.byCounterparty', 'Gönderici / Alıcıya göre')}
        </Button>
        <Button
          size="sm"
          variant={view === 'entries' ? 'default' : 'outline'}
          onClick={() => {
            setView('entries');
            setPage(1);
            setSelected(null);
          }}
        >
          {t('bankMatching.individual', 'Tek hareket')}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-60 flex-1">
          <Search className="text-muted-foreground absolute left-3 top-2.5 h-4 w-4" />
          <Input
            aria-label={t('bankMatching.search', 'Hareket ara')}
            value={search}
            onChange={(e) => changeFilter(() => setSearch(e.target.value))}
            placeholder={t('bankMatching.searchPlaceholder', 'İsim, açıklama veya referans ara')}
            className="pl-9"
          />
        </div>
        <select
          aria-label={t('bankMatching.status', 'Durum')}
          className={selectClass}
          value={status}
          onChange={(e) => changeFilter(() => setStatus(e.target.value as typeof status))}
        >
          <option value="pending">{t('bankMatching.pending', 'Eşleştirme bekleyen')}</option>
          <option value="completed">{t('bankMatching.completed', 'Tamamlanan')}</option>
          <option value="all">{t('bankMatching.all', 'Tümü')}</option>
        </select>
        <select
          aria-label={t('bankMatching.currency', 'Döviz')}
          className={selectClass}
          value={currency}
          onChange={(e) => changeFilter(() => setCurrency(e.target.value))}
        >
          <option value="">{t('bankMatching.allCurrencies', 'Tüm dövizler')}</option>
          {['TRY', 'EUR', 'USD', 'GBP'].map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          aria-label={t('bankMatching.bank', 'Banka hesabı')}
          className={`${selectClass} max-w-72`}
          value={bankId}
          onChange={(e) => changeFilter(() => setBankId(e.target.value))}
        >
          <option value="">{t('bankMatching.allBanks', 'Tüm banka hesapları')}</option>
          {viewData?.banks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      {view === 'groups' ? (
        <GroupedBankMatching
          key={`${status}|${currency}|${bankId}|${search}`}
          data={groups.data}
          isLoading={groups.isLoading}
          isFetching={groups.isFetching}
          error={groups.error?.message}
          scope={filters}
          page={page}
          onPageChange={setPage}
          caris={caris.data}
          canSave={canSave}
          canCreateCari={canCreateCari}
          readOnlyPreview={readOnlyPreview}
          onCreateCari={() => setNewCariOpen(true)}
          onIndividualView={(name) => {
            setView('entries');
            setSearch(name);
            setPage(1);
            setSelected(null);
          }}
        />
      ) : (
        <div
          className={`grid items-start gap-4 ${selected ? 'xl:grid-cols-[minmax(0,1fr)_400px]' : ''}`}
        >
          <section className="bg-card min-w-0 rounded-lg border">
            {list.error && (
              <p role="alert" className="text-destructive p-4 text-sm">
                {list.error.message}
              </p>
            )}
            {list.isLoading ? (
              <div className="flex justify-center p-12">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left">{t('bankMatching.date', 'Tarih')}</th>
                      <th className="px-4 py-3 text-left">
                        {t('bankMatching.counterparty', 'Gönderici / Alıcı')}
                      </th>
                      <th className="px-4 py-3 text-right">
                        {t('bankMatching.amount', 'Banka tutarı')}
                      </th>
                      <th className="px-4 py-3 text-right">{t('bankMatching.action', 'İşlem')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data?.items.map((row) => (
                      <tr
                        key={row.id}
                        className={`border-b last:border-0 ${selected?.id === row.id ? 'bg-primary/5' : 'hover:bg-muted/30'}`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 align-top">
                          {day(row.date)}
                          <p className="text-muted-foreground mt-1 text-xs">{row.currency}</p>
                        </td>
                        <td className="max-w-md px-4 py-3">
                          <p className="font-medium">
                            {row.counterpartyText ||
                              t('bankMatching.unnamed', 'Açıklamada isim yok')}
                          </p>
                          <p
                            className="text-muted-foreground mt-1 line-clamp-2 text-xs"
                            title={row.description}
                          >
                            {row.description}
                          </p>
                          <p className="text-muted-foreground mt-1 text-xs">{row.bankName}</p>
                          {row.status === 'completed' && (
                            <p className="mt-1 text-xs text-emerald-700">
                              {row.resolution?.targetName ||
                                t('bankMatching.nonCari', 'Cari dışı tamamlandı')}
                            </p>
                          )}
                        </td>
                        <td
                          className={`whitespace-nowrap px-4 py-3 text-right align-top font-medium ${row.flow === 'incoming' ? 'text-emerald-700' : 'text-rose-700'}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {row.flow === 'incoming' ? (
                              <ArrowDownLeft className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            )}
                            {money(row.amount, row.currency)}
                          </span>
                          <p className="mt-1 text-xs font-normal">
                            {row.flow === 'incoming'
                              ? t('bankMatching.incoming', 'Banka girişi')
                              : t('bankMatching.outgoing', 'Banka çıkışı')}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-right align-top">
                          <Button
                            size="sm"
                            variant={selected?.id === row.id ? 'default' : 'outline'}
                            aria-label={`${t('bankMatching.match', 'Eşleştir')}: ${row.counterpartyText}`}
                            onClick={() => choose(row)}
                          >
                            {row.status === 'pending'
                              ? t('bankMatching.match', 'Eşleştir')
                              : t('bankMatching.view', 'Görüntüle')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {!list.data?.items.length && (
                      <tr>
                        <td colSpan={4} className="text-muted-foreground p-12 text-center">
                          {t('bankMatching.empty', 'Bu filtrelerde hareket bulunamadı.')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="text-muted-foreground flex items-center justify-between gap-3 border-t px-4 py-3 text-xs">
              <span>
                {list.data?.pagination.total ?? 0} {t('bankMatching.records', 'hareket')}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1 || list.isFetching}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t('bankMatching.previous', 'Önceki')}
                </Button>
                <span>
                  {page} / {Math.max(1, list.data?.pagination.totalPages ?? 1)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= (list.data?.pagination.totalPages ?? 1) || list.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('bankMatching.next', 'Sonraki')}
                </Button>
              </div>
            </div>
          </section>
          {selected && (
            <aside
              ref={detailRef}
              className="bg-card order-first min-w-0 scroll-mt-4 space-y-4 rounded-lg border p-5 xl:sticky xl:top-4 xl:order-none xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-semibold">{t('bankMatching.detail', 'Eşleştirme detayı')}</h2>
                  <p className="text-muted-foreground mt-1 text-sm">{selected.counterpartyText}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(null)}
                  aria-label={t('bankMatching.close', 'Detayı kapat')}
                >
                  ×
                </Button>
              </div>
              <div className="bg-muted/40 rounded-md p-3">
                <p className="text-lg font-semibold">{money(selected.amount, selected.currency)}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {day(selected.date)} ·{' '}
                  {selected.flow === 'incoming'
                    ? t('bankMatching.incoming', 'Banka girişi')
                    : t('bankMatching.outgoing', 'Banka çıkışı')}
                </p>
                <p className="mt-2 break-words text-sm">{selected.description}</p>
              </div>
              {selected.status === 'pending' && (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  {t(
                    `bankMatching.reasons.${selected.reason.replace('review:', '')}`,
                    'Karşı cari seçimi gerekiyor.'
                  )}
                </p>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer font-medium">
                  {t('bankMatching.sourceDetails', 'Valör, referans ve kaynak')}
                </summary>
                <dl className="mt-3 space-y-2 break-words">
                  {[
                    [t('bankMatching.valueDate', 'Valör'), day(selected.valueDate)],
                    [t('bankMatching.timestamp', 'Tarih / Saat'), selected.timestamp],
                    [t('bankMatching.reference', 'Referans'), selected.reference],
                    ['IBAN', selected.iban],
                    [t('bankMatching.channel', 'Kanal'), selected.channel],
                    [
                      t('bankMatching.source', 'Kaynak'),
                      `${selected.sourceFile} · ${selected.excelRow ?? ''}`,
                    ],
                    [
                      t('bankMatching.bankBalance', 'Banka ara bakiyesi'),
                      `${selected.bankBalance} ${selected.currency}`,
                    ],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              </details>
              {selected.status === 'completed' ? (
                <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <p className="font-medium">{t('bankMatching.completed', 'Tamamlanan')}</p>
                  <p>
                    {selected.resolution?.targetName ||
                      t('bankMatching.nonCari', 'Cari dışı tamamlandı')}
                  </p>
                  <p>{selected.resolution?.note}</p>
                  {selected.resolution?.journalEntryId && canViewAccounting && (
                    <Link
                      className="text-primary block underline"
                      to={`/trading/accounting/journal-entries/${selected.resolution.journalEntryId}`}
                    >
                      {t('bankMatching.viewJournal', 'Yevmiye kaydını görüntüle')}
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  <fieldset className="space-y-2">
                    <legend className="mb-2 text-xs font-medium">
                      {t('bankMatching.saveAs', 'Nasıl tamamlanacak?')}
                    </legend>
                    {(
                      [
                        {
                          value: 'create',
                          label: t(
                            'bankMatching.createMovement',
                            'Cari ile eşleştir — yeni cari hareketi oluştur'
                          ),
                        },
                        {
                          value: 'account',
                          label: t(
                            'bankMatching.matchAccount',
                            'Hesap numarası ile eşleştir — yevmiye oluştur'
                          ),
                        },
                        {
                          value: 'existing',
                          label: t('bankMatching.linkExisting', 'Mevcut cari hareketiyle eşleştir'),
                        },
                        {
                          value: 'nonCari',
                          label: t(
                            'bankMatching.completeNonCari',
                            'Cari dışı işlem olarak tamamla'
                          ),
                        },
                      ] as const
                    ).map((o) => (
                      <label
                        key={o.value}
                        className="flex cursor-pointer items-start gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name="bank-match-mode"
                          value={o.value}
                          checked={mode === o.value}
                          onChange={() => {
                            setMode(o.value);
                            setAllowDuplicate(false);
                            setError('');
                          }}
                          disabled={save.isPending || accountSaving}
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </fieldset>
                  {mode === 'account' && (
                    <AccountBankMatching
                      key={selected.id}
                      bankMovementId={selected.id}
                      readOnlyPreview={readOnlyPreview}
                      onPendingChange={setAccountSaving}
                      onSaved={(journalEntryId, targetName, savedNote) => {
                        setSelected({
                          ...selected,
                          status: 'completed',
                          resolution: {
                            mode: 'account',
                            targetName,
                            note: savedNote,
                            amount: selected.amount,
                            savedAt: new Date(),
                            movementId: '',
                            journalEntryId,
                          },
                        });
                      }}
                    />
                  )}
                  {(mode === 'create' || mode === 'existing') && (
                    <>
                      <div className="space-y-2">
                        <Label>{t('bankMatching.target', 'Karşı cari')}</Label>
                        <Combobox
                          ariaLabel={t('bankMatching.target', 'Karşı cari')}
                          options={options}
                          value={targetValue}
                          onValueChange={(v) => {
                            setTargetValue(v);
                            setExistingId('');
                            setAllowDuplicate(false);
                          }}
                          placeholder={t('bankMatching.selectCari', 'Cari seçin')}
                          searchPlaceholder={t('bankMatching.searchCari', 'Cari adı veya kodu ara')}
                          emptyText={t(
                            'bankMatching.noCari',
                            'Cari bulunamadı. Yeni Cari ile oluşturabilirsiniz.'
                          )}
                          disabled={save.isPending}
                        />
                        {canCreateCari && (
                          <button
                            type="button"
                            className="text-primary text-xs underline"
                            onClick={() => setNewCariOpen(true)}
                          >
                            {t('bankMatching.missingCari', 'Cari kartı yoksa yeni oluştur')}
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank-match-amount">
                          {t('bankMatching.cariAmount', 'Cari tutarı')} ({selected.currency})
                        </Label>
                        <NumberInput
                          id="bank-match-amount"
                          value={amount ?? ''}
                          onValueChange={(v) => {
                            setAmount(v);
                            setExistingId('');
                            setAllowDuplicate(false);
                          }}
                          decimalScale={2}
                          allowNegative={false}
                          disabled={save.isPending}
                        />
                        {remainder > 0 && (
                          <p className="text-xs text-amber-700">
                            {t('bankMatching.remainder', 'Cari dışında kalan tutar')}:{' '}
                            {money(remainder, selected.currency)}.{' '}
                            {t(
                              'bankMatching.remainderNote',
                              'Faiz/vergi gibi kalan kısmı açıklayın.'
                            )}
                          </p>
                        )}
                      </div>
                      {preview.isFetching && (
                        <p className="text-muted-foreground flex items-center gap-2 text-xs">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {t('bankMatching.previewLoading', 'Cari etkisi hesaplanıyor…')}
                        </p>
                      )}
                      {preview.error && (
                        <p role="alert" className="text-destructive text-sm">
                          {preview.error.message}
                        </p>
                      )}
                      {preview.data && (
                        <div className="space-y-1 rounded-md border p-3 text-xs">
                          <p className="font-medium">
                            {preview.data.direction === 'debit'
                              ? t('bankMatching.debit', 'Cari borçlandırılacak')
                              : t('bankMatching.credit', 'Cari alacaklandırılacak')}
                            : {money(preview.data.amount, preview.data.currency)}
                          </p>
                          <p>
                            {t('bankMatching.balanceBefore', 'Mevcut cari bakiyesi')}:{' '}
                            {money(preview.data.balanceBefore, preview.data.currency)}
                          </p>
                          <p>
                            {t('bankMatching.balanceAfter', 'Kaydetme sonrası bakiye')}:{' '}
                            {money(
                              mode === 'existing'
                                ? preview.data.balanceBefore
                                : preview.data.balanceAfterCreate,
                              preview.data.currency
                            )}
                          </p>
                          {mode === 'existing' && (
                            <p className="text-muted-foreground">
                              {t(
                                'bankMatching.existingNoChange',
                                'Mevcut hareket bağlantısı yeni borç/alacak oluşturmaz.'
                              )}
                            </p>
                          )}
                          <p className="text-muted-foreground">
                            {t('bankMatching.tryValue', 'İşlem tarihli TL karşılığı')}:{' '}
                            {money(preview.data.tryAmount, 'TRY')}
                          </p>
                        </div>
                      )}
                      {candidates.length > 0 && (
                        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                          <p className="font-medium">
                            {t(
                              'bankMatching.existingFound',
                              'Aynı döviz ve tutarda mevcut hareket bulundu.'
                            )}
                          </p>
                          {mode === 'existing' ? (
                            <select
                              aria-label={t(
                                'bankMatching.existingMovement',
                                'Mevcut cari hareketi'
                              )}
                              className={`${selectClass} w-full`}
                              value={existingId}
                              onChange={(e) => setExistingId(e.target.value)}
                            >
                              <option value="">
                                {t('bankMatching.selectExisting', 'Mevcut hareket seçin')}
                              </option>
                              {candidates.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {day(c.date)} · {c.voucherNo} · {c.description}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <label className="flex items-start gap-2 text-xs">
                              <input
                                type="checkbox"
                                checked={allowDuplicate}
                                onChange={(e) => setAllowDuplicate(e.target.checked)}
                              />
                              <span>
                                {t(
                                  'bankMatching.duplicateConfirm',
                                  'Bunun ayrı bir ödeme olduğunu doğruluyorum; yeni hareket oluştur.'
                                )}
                              </span>
                            </label>
                          )}
                        </div>
                      )}
                      {mode === 'existing' && preview.data && !candidates.length && (
                        <p className="text-muted-foreground text-xs">
                          {t(
                            'bankMatching.noExisting',
                            'Bu caride aynı döviz ve tutarda, işlem tarihinin 7 gün öncesi/sonrasında hareket bulunamadı.'
                          )}
                        </p>
                      )}
                    </>
                  )}
                  {mode !== 'account' && (
                    <>
                      {mode === 'nonCari' && (
                        <p className="text-muted-foreground text-xs">
                          {t(
                            'bankMatching.nonCariHelp',
                            'Bu seçenek yalnızca incelemeyi kapatır; yevmiye oluşturmaz. Muhasebe kaydı gerekiyorsa hesap numarası ile eşleştirin.'
                          )}
                        </p>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="bank-match-note">
                          {t('bankMatching.note', 'Açıklama')}
                          {needsNote ? ' *' : ''}
                        </Label>
                        <Textarea
                          id="bank-match-note"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          maxLength={1000}
                          rows={3}
                          disabled={save.isPending}
                          placeholder={
                            mode === 'nonCari'
                              ? t(
                                  'bankMatching.nonCariNote',
                                  'Örn. yevmiyesi zaten kaydedilmiş hesaplar arası virman; kayıt referansını belirtin'
                                )
                              : t('bankMatching.notePlaceholder', 'Eşleştirmeye ilişkin not')
                          }
                        />
                      </div>
                      {(error || save.error) && (
                        <p
                          role="alert"
                          className="bg-destructive/10 text-destructive rounded-md p-3 text-sm"
                        >
                          {error || save.error?.message}
                        </p>
                      )}
                      {readOnlyPreview && (
                        <p className="text-muted-foreground text-xs">
                          {t(
                            'bankMatching.readOnlyPreview',
                            'Bu önizleme salt okunurdur. Eşleştirmeler canlı uygulamada kaydedilebilir.'
                          )}
                        </p>
                      )}
                      {!canCreateCustomer && (
                        <p className="text-muted-foreground text-xs">
                          {t(
                            'bankMatching.noPermission',
                            'Kaydetmek için cari oluşturma yetkisi gerekir.'
                          )}
                        </p>
                      )}
                      <Button className="w-full" disabled={!valid} onClick={submit}>
                        {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('bankMatching.save', 'Eşleştirmeyi Kaydet')}
                      </Button>
                    </>
                  )}
                </>
              )}
            </aside>
          )}
        </div>
      )}
      <NewCariDialog open={newCariOpen} onOpenChange={setNewCariOpen} onSelect={setCreateType} />
      <CariCreateModals
        createType={createType}
        typeLabel={
          types.data?.find((tp) => tp.code === createType)
            ? cariTypeLabel(types.data.find((tp) => tp.code === createType)!, i18n.language)
            : undefined
        }
        onClose={() => {
          setCreateType(null);
          utils.tradingCari.list.invalidate();
        }}
      />
    </div>
  );
}
